import { useCallback, useEffect, useRef, useState } from "react";
import type { Job } from "@eagledispatch/shared";
import { apiFetch, getSession } from "../../lib/api";
import { submitPunch } from "../../lib/punch";
import { usePunchState, decideLanding } from "../../lib/punch-state";
import {
  makeHandoffRef,
  armHandoff,
  consumeHandoff,
  clearHandoff,
} from "../../lib/punch-handoff";
import AppChrome from "../../components/AppChrome";
import Toast, { type ToastData } from "../../components/Toast";
import DispatcherSignOut from "../../components/DispatcherSignOut";
import LunchScreen from "../../components/LunchScreen";
import DriverLogin from "./DriverLogin";
import PunchScreen from "./PunchScreen";
import WaitingScreen from "./WaitingScreen";
import ActiveJob from "./ActiveJob";
import JobComplete from "./JobComplete";

type DriverPhase =
  | { name: "login" }
  | { name: "punch"; driverName: string; loginScanUid: string | null }
  | { name: "waiting" }
  | { name: "active"; job: Job }
  | { name: "complete"; job: Job };

type Notice = { kind: "cancelled" | "reassigned" | "paused"; message: string } | null;

export default function DriverApp({ onLogout }: { onLogout: () => void }) {
  const [phase, setPhase] = useState<DriverPhase>({ name: "login" });
  const [currentDriverId, setCurrentDriverId] = useState<string | null>(null);
  const [driverDisplayName, setDriverDisplayName] = useState<string>("");
  const [notice, setNotice] = useState<Notice>(null);
  const [lunchStartedAt, setLunchStartedAt] = useState<string | null>(null);
  const [lunchInFlight, setLunchInFlight] = useState(false);
  const [clockOutOpen, setClockOutOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const toastId = useRef(0);
  const noticeTimerRef = useRef<number | null>(null);
  // SINGLE-SCAN-MORNINGS (B): `hydrated` distinguishes a server answer from
  // the initial NO_SHIFT default — the UNKNOWN state that must fail toward
  // Waiting. (C): the one-shot login-scan handoff lives in a plain ref.
  const { state: toolbarState, latest, lunchTakenToday, hydrated, refresh } = usePunchState();
  const handoffRef = useRef(makeHandoffRef());
  const handoff = handoffRef.current;

  useEffect(() => {
    if (latest?.punchType === "LUNCH_OUT") setLunchStartedAt(latest.punchedAt);
    else if (latest) setLunchStartedAt(null);
  }, [latest]);

  // SINGLE-SCAN-MORNINGS (B) — deferred landing. handleLoggedIn arms this
  // ONLY when login raced the /timeclock/latest fetch (unknown → Waiting).
  // The effect resolves the deferral EXACTLY once, on the first server
  // answer: NO_SHIFT → punch (consuming the handoff); WORKING/ON_LUNCH →
  // Waiting stands. Anything that claims the screen first (a job arriving,
  // any later waiting transition) DISARMS the deferral — the punch screen is
  // never forced on a driver who reached Waiting by any other route. No
  // armed deferral → no effect → the punch screen is reachable only via the
  // login gate's direct decision or this one-shot resolution.
  const landingDeferredRef = useRef(false);
  const parkedOnWaiting = phase.name === "waiting";
  useEffect(() => {
    if (!landingDeferredRef.current) return;
    if (!parkedOnWaiting) {
      landingDeferredRef.current = false;
      return;
    }
    if (!hydrated) return;
    landingDeferredRef.current = false;
    if (toolbarState === "NO_SHIFT") {
      // Consumed OUTSIDE the updater (updaters double-run under StrictMode);
      // consumption semantics unchanged — the UID is consumed either way,
      // whichever branch the updater takes.
      const uid = consumeHandoff(handoff);
      // AUDIT FOLD (BUG #2): functional-updater commit — a concurrent phase
      // write (the 30s/focus poll) landing between the guard above and this
      // commit WINS the race atomically (non-waiting phases map to
      // themselves) instead of being clobbered by the punch screen.
      setPhase((p) =>
        p.name === "waiting"
          ? { name: "punch", driverName: driverDisplayName, loginScanUid: uid }
          : p,
      );
    }
  }, [parkedOnWaiting, toolbarState, hydrated, driverDisplayName, handoff]);

  // SINGLE-SCAN-MORNINGS (C) — the handoff never outlives the session:
  // unmount (kickout/logout/epoch remount) drops any unconsumed UID.
  useEffect(() => () => clearHandoff(handoff), [handoff]);

  useEffect(() => {
    let cancelled = false;
    const session = getSession();
    if (session?.user?.driverId) setCurrentDriverId(session.user.driverId);
    if (session?.user?.name) setDriverDisplayName(session.user.name);
    // SPEC-74 BUG #1: /drivers/me/active-job is DRIVER-only. The iOS shell
    // boots /driver regardless of role, so a DISPATCHER/OFFICE session here
    // 403s and every retry (button, 30s poll, focus) repeats it — the
    // "Can't reach dispatch" toast is permanent and Retry is dead. Gate on
    // the SYNCHRONOUS cached role: a driver proceeds exactly as before; a
    // non-driver session skips the fetch (App.tsx restore routing moves it
    // off /driver); no session = login surface handles it (fetch would 401
    // into waiting today — skipping is equivalent and quieter).
    if (session?.user?.role === "DRIVER") {
      apiFetch<{ job: Job | null }>("/api/v1/drivers/me/active-job")
        .then((res) => {
          if (cancelled) return;
          setPhase(res.job ? { name: "active", job: res.job } : { name: "waiting" });
        })
        .catch(() => {
          if (!cancelled && getSession()) setPhase({ name: "waiting" });
        });
    }
    return () => { cancelled = true; };
  }, []);

  const handleLoggedIn = useCallback((driverId: string, loginScanUid?: string | null) => {
    setCurrentDriverId(driverId);
    const name = getSession()?.user?.name || "Driver";
    setDriverDisplayName(name);
    // SINGLE-SCAN-MORNINGS (C): arm the one-shot handoff from the login-gate
    // scan (undefined/null = this login never scanned — arm nothing).
    if (loginScanUid) armHandoff(handoff, loginScanUid);
    // SINGLE-SCAN-MORNINGS (C)+(B): consume the armed handoff ONLY when this
    // login lands on the punch screen. On a waiting landing the UID stays
    // armed for the deferred-landing effect (which consumes it if and when
    // the resolved NO_SHIFT answer routes to punch) — consuming it here too
    // would strand a fresh-NO_SHIFT driver into a SECOND scan. An on-shift
    // driver never reaches punch and their armed UID dies unread.
    if (decideLanding(toolbarState, hydrated) === "waiting") {
      // A hydrated waiting-landing is a DECISION (on-shift driver): the
      // deferred-landing effect must never re-decide. An unknown waiting-
      // landing (login raced the fetch) ARMS the deferred resolution.
      landingDeferredRef.current = !hydrated;
      setPhase({ name: "waiting" });
      // The usePunchState mount fetch ran BEFORE login (sessionless 401 →
      // fail-closed), so re-pull /timeclock/latest NOW that the session is
      // live — this is the data the deferred-landing effect resolves on.
      void refresh();
    } else {
      const armedUid = consumeHandoff(handoff);
      setPhase({ name: "punch", driverName: name, loginScanUid: armedUid });
    }
  }, [toolbarState, hydrated, handoff, refresh]);
  const handleJob = useCallback((job: Job) => {
    setPhase(job.state === "NEW" || job.state === "IN_PROGRESS" ? { name: "active", job } : { name: "waiting" });
  }, []);
  const handleFinished = useCallback(() => setPhase({ name: "waiting" }), []);
  const flashNotice = useCallback((kind: "cancelled" | "reassigned" | "paused") => {
    if (noticeTimerRef.current !== null) window.clearTimeout(noticeTimerRef.current);
    setNotice({ kind, message: kind === "cancelled" ? "Job was cancelled by dispatch." : kind === "reassigned" ? "Job was reassigned to another driver." : "Job was paused by dispatch — returning to waiting." });
    noticeTimerRef.current = window.setTimeout(() => { setNotice(null); noticeTimerRef.current = null; }, 5000);
  }, []);
  useEffect(() => () => { if (noticeTimerRef.current !== null) window.clearTimeout(noticeTimerRef.current); }, []);
  const handleCancelled = useCallback(() => { flashNotice("cancelled"); setPhase({ name: "waiting" }); }, [flashNotice]);
  const handleReassigned = useCallback(() => { flashNotice("reassigned"); setPhase({ name: "waiting" }); }, [flashNotice]);
  const handlePaused = useCallback(() => { flashNotice("paused"); setPhase({ name: "waiting" }); }, [flashNotice]);
  const handleKickout = useCallback(() => {
    // SINGLE-SCAN-MORNINGS (C): a stale handed UID must never survive into
    // the next session (wrong-device risk) — drop it on ANY phase exit.
    clearHandoff(handoff);
    onLogout();
    setPhase({ name: "login" });
  }, [onLogout, handoff]);
  const pushToast = useCallback((message: string, kind: "success" | "error" = "error") => {
    const id = ++toastId.current;
    const duration = kind === "error" ? 10000 : 4000;
    setToasts((prev) => [...prev, { id, message, kind, duration }]);
  }, []);
  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleLunchOut = useCallback(async () => {
    if (lunchInFlight || toolbarState !== "WORKING") return;
    setLunchInFlight(true);
    try {
      const result = await submitPunch("LUNCH_OUT");
      if (result.ok) { setLunchStartedAt(result.punchedAt ?? new Date().toISOString()); await refresh(); }
      else {
        pushToast(result.message || "Couldn't reach the server — try again", "error");
        await refresh(); // SPEC-65: re-pull lunchTakenToday after a rejection
      }
    } finally { setLunchInFlight(false); }
  }, [lunchInFlight, pushToast, refresh, toolbarState]);
  const handleLunchIn = useCallback(async () => {
    if (lunchInFlight) return;
    setLunchInFlight(true);
    try {
      const result = await submitPunch("LUNCH_IN");
      if (result.ok) { setLunchStartedAt(null); await refresh(); }
      else {
        pushToast(result.message || "Couldn't reach the server — try again", "error");
        await refresh(); // SPEC-65: re-pull lunchTakenToday after a rejection
      }
    } finally { setLunchInFlight(false); }
  }, [lunchInFlight, pushToast, refresh]);
  const handleClockOut = useCallback(() => setClockOutOpen(true), []);
  const handleClockOutComplete = useCallback(() => {
    setClockOutOpen(false);
    // SINGLE-SCAN-MORNINGS (C): the session dies here — the handoff dies with
    // it (the sessionEpoch remount below mounts a FRESH DriverApp whose
    // handoffRef starts empty; this clear keeps the doomed-instance window
    // clean too).
    clearHandoff(handoff);
    // Fold F4: deliberately NO setLunchStartedAt(null) / setPhase writes here.
    // The sessionEpoch remount (onLogout → logout() → notifyUnauthorized())
    // tears this instance down and mounts a fresh DriverApp with initial
    // state — any additional writes are doomed-instance renders. The modal
    // close (above) is the only write needed.
    onLogout();
  }, [onLogout, handoff]);  const handleClockOutCancel = useCallback(async () => {
    setClockOutOpen(false);
    // Audit r3 BUG #1 (toast-buried-by-modal): a pre-existing lunch-error toast
    // can be hidden by the z:1000 modal and auto-dismiss unseen. Cancel is the
    // only path where DriverApp survives without a re-sync — refresh the punch
    // state so the toolbar reflects the server truth (e.g. still WORKING after
    // a failed LUNCH_OUT). Complete needs no refresh: the sessionEpoch remount
    // mounts a fresh DriverApp whose usePunchState fetches fresh state.
    await refresh();
  }, [refresh]);

  if (phase.name === "login") return <DriverLogin onLoggedIn={handleLoggedIn} />;

  const toolbar = (children: React.ReactNode, showClockOut: boolean) => (
    <AppChrome role="DRIVER" toolbarState={lunchInFlight ? "NO_SHIFT" : toolbarState} lunchTaken={lunchTakenToday} onLunchOut={() => void handleLunchOut()} onLunchIn={() => void handleLunchIn()} onClockOut={() => void handleClockOut()} showClockOut={showClockOut}>
      {children}
    </AppChrome>
  );

  let content: React.ReactNode;
  if (phase.name === "punch") {
    // PunchScreen punched (IN only — OUT always scans) → the server's latest
    // punch changed. usePunchState fetched at DriverApp mount (PIN login,
    // BEFORE this screen), so without this refresh the toolbar would keep its
    // stale pre-punch state all session (greyed Lunch after CLOCK IN).
    content = <PunchScreen driverName={phase.driverName || driverDisplayName} loginScanUid={phase.loginScanUid} onDone={() => { void refresh(); setPhase({ name: "waiting" }); }} onKickout={handleKickout} />;
  } else if (phase.name === "waiting") {
    content = <WaitingScreen onJob={handleJob} notice={notice} onDismissNotice={() => setNotice(null)} onToast={pushToast} />;
  } else if (phase.name === "active") {
    content = <ActiveJob job={phase.job} currentDriverId={currentDriverId!} onFinish={(job) => setPhase({ name: "complete", job })} onCancelled={handleCancelled} onReassigned={handleReassigned} onPaused={handlePaused} />;
  } else {
    content = <JobComplete jobId={phase.job.id} jobNumber={phase.job.jobNumber} jobType={phase.job.jobType} erReturnMinutes={phase.job.erReturnMinutes} initialTicketPhotoAt={phase.job.ticketPhotoAt} currentDriverId={currentDriverId!} onBack={() => setPhase({ name: "active", job: phase.job })} onFinished={handleFinished} onCancelled={handleCancelled} onReassigned={handleReassigned} onPaused={handlePaused} />;
  }
  // Hidden (not disabled) mid-job AND while a lunch punch is in flight — the
  // latter closes the audit BUG #1 race (Lunch tap → Clock Out tap before the
  // punch resolves → modal z:1000 would occlude the failure toast at z:200).
  // The moment lunchInFlight resolves, the button returns.
  const showClockOut = phase.name === "waiting" && !lunchInFlight;
  return <>
    {toolbar(content, showClockOut)}
    {lunchStartedAt && <LunchScreen lunchStartedAt={lunchStartedAt} onEndLunch={() => void handleLunchIn()} />}
    {clockOutOpen && (
      <DispatcherSignOut role="DRIVER" onComplete={handleClockOutComplete} onCancel={handleClockOutCancel} />
    )}
    <div className="toast-stack">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={dismissToast} />
      ))}
    </div>
  </>;
}
